<template>
	<Card
		:loading="calendarViewRef?.loading"
		bordered
		class="overflow-hidden"
		content-class="flex h-full min-h-0 flex-col p-0!"
	>
		<div class="border-divider border-b px-4 py-3">
			<CalendarToolbar
				v-model:office-id="selectedOfficeId"
				v-model:patient-id="selectedPatientId"
				:view="calendarViewRef?.calendarView ?? initialView"
				:period="calendarViewRef?.periodDisplay ?? defaultPeriodDisplay"
				:show-navigation
				:show-view-switcher
				@prev="calendarViewRef?.navigateCalendar('prev')"
				@next="calendarViewRef?.navigateCalendar('next')"
				@today="calendarViewRef?.navigateCalendar('today')"
				@update:view="calendarViewRef?.changeCalendarView($event)"
				@saved="calendarViewRef?.reloadAppointments()"
			/>
		</div>

		<div :class="bodyClass">
			<CalendarView ref="calendarViewRef" :initial-view :selected-office-id :selected-patient-id />
		</div>
	</Card>
</template>

<script setup lang="ts">
import type { CalendarPeriodDisplay, CalendarViewType } from "@/components/calendar/CalendarToolbar.vue"
import { nextTick, ref, watch } from "vue"
import CalendarToolbar from "@/components/calendar/CalendarToolbar.vue"
import CalendarView from "@/components/calendar/CalendarView.vue"
import Card from "@/components/common/Card.vue"
import dayjs from "@/utils/dayjs"

const props = withDefaults(
	defineProps<{
		/** Classi del wrapper attorno a `CalendarView` (es. altezza fissa in dashboard). */
		bodyClass?: string
		/** Vista iniziale / forzata del calendario. */
		initialView?: CalendarViewType
		/** Mostra i pulsanti prev / oggi / next nella toolbar. */
		showNavigation?: boolean
		/** Mostra la select per cambiare vista nella toolbar. */
		showViewSwitcher?: boolean
	}>(),
	{
		bodyClass: "min-h-0 grow",
		initialView: "timeGridWeek",
		showNavigation: true,
		showViewSwitcher: true
	}
)

const calendarViewRef = ref<InstanceType<typeof CalendarView> | null>(null)
const selectedOfficeId = ref<number | null>(null)
const selectedPatientId = ref<number | null>(null)

const defaultPeriodDisplay: CalendarPeriodDisplay = {
	mode: "range",
	primary: dayjs().format("D MMMM"),
	secondary: dayjs().format("YYYY")
}

watch(
	() => props.initialView,
	async view => {
		await nextTick()
		if (calendarViewRef.value && calendarViewRef.value.calendarView !== view) {
			calendarViewRef.value.changeCalendarView(view)
		}
	}
)

defineExpose({
	changeCalendarView: (view: CalendarViewType) => calendarViewRef.value?.changeCalendarView(view),
	navigateCalendar: (action: "prev" | "next" | "today") => calendarViewRef.value?.navigateCalendar(action),
	reloadAppointments: () => calendarViewRef.value?.reloadAppointments(),
	get calendarView() {
		return calendarViewRef.value?.calendarView
	},
	get loading() {
		return calendarViewRef.value?.loading
	}
})
</script>
